import { color } from "@smartsheet/design-tokens-react";
import { themed, ThemeMode } from "@smartsheet/sds-theme-internal";

/**
 * Apply this to any element that has a focus ring created with a ::before pseudoelement.
 * {@see createFocusRing} for more details.
 * TODO move this to createFocusRingStyles
 */
export const FOCUS_USING_BEFORE_CLASS_NAME = "lodestar--focus-using-before";

/**
 * Apply this to any element that has a focus ring created with a real
 * DOM element (as opposed to a ::before pseudoelement).
 * {@see createFocusRing} for more details.
 * TODO move this to createFocusRingStyles
 */
export const FOCUS_USING_ELEMENT_CLASS_NAME = "lodestar--focus-using-element";

export interface CreateFocusRingProps {
    /**
     * Distance away from the component. This will be applied as negative top/right/bottom/left.
     */
    offset: number;

    /**
     * Border-radius. If number, assume px. If string, assumes a valid CSS radius value ("initial,
     * inherit, etc.") or that units come with it (32px, etc.).
     */
    radius: number | string;

    /**
     * The ring's color.
     * This is useful in theming, but shouldn't be overridden usually.
     */
    theme: ThemeMode;

    /**
     * border-width
     */
    borderWidth?: number;

    /**
     * If specified, overrides the default colors from the theme.
     */
    colorOverride?: string;
    /**
     * Defaults to "focus". If provided, this pseudoclass will be used to apply the focus ring.
     */
    focusSelector?: "focus" | "focus-within" | "focus--visible";

    /**
     * Defaults to false. If true, will include the :focus modifier on the root selector.
     * This allows the consumer to apply the focusRing directly to a focusable element more easily.
     * In lodestar-core, this mixin is commonly applied to an element that doesn't actually receive focus.
     * example: Toggle, Select.
     */
    includeFocusPseudoClass?: boolean;

    /**
     * Position to apply to the root element.
     */
    position?: "relative" | "absolute";

    /**
     * Which pseudoelement to use for the ring. Defaults to ::before.
     * Useful in the case where you have 2 rings on a component i.e. select.
     */
    pseudoElement?: "before" | "after";
}

const focusRingTheme = themed({
    color: color("color--border--surfaces--focus"),
});

/**
 * @deprecated please use createFocusRingStyles instead.
 */
export const getFocusRingStyles = ({
    colorOverride: color,
    theme,
    offset,
    radius: borderRadius,
    // TODO we should default this to true before we expose this.
    includeFocusPseudoClass = false,
    borderWidth = 2,
    position = "relative",
    pseudoElement = "before",
    focusSelector = "focus",
}: CreateFocusRingProps): string => `
    & {
        position: ${position};
        outline: none;
    }

    &:${focusSelector} {
        outline: none;
    }

    &${includeFocusPseudoClass ? `:${focusSelector}` : ""}::${pseudoElement} {
        content: "";
        display: block;
        position: absolute;
        top: -${offset}px;
        right: -${offset}px;
        bottom: -${offset}px;
        left: -${offset}px;
        border: ${borderWidth}px solid ${color || focusRingTheme.color(theme)};
        border-radius: ${typeof borderRadius === "string" ? borderRadius : `${borderRadius}px`};
        pointer-events: none;
    }
`;
